// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/NecessidadeReposicao.tsx
//  Relatório de produtos abaixo do estoque mínimo
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { useEstoqueReposicao } from '@/features/estoque/hooks/useEstoquePosicao';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const PAGE_SIZES = [10, 20, 50] as const;

type OrdemCriticidade = 'critico' | 'alto' | 'moderado';

function calcCriticidade(saldoAtual: number, estoqueMinimo: number): { ordem: number; label: string; className: string } {
  const pct = estoqueMinimo > 0 ? (saldoAtual / estoqueMinimo) * 100 : 0;
  if (pct <= 0)  return { ordem: 0, label: 'Crítico',  className: 'bg-red-500/10 text-red-600' };
  if (pct < 50)  return { ordem: 1, label: 'Alto',     className: 'bg-orange-500/10 text-orange-600' };
  return          { ordem: 2, label: 'Moderado', className: 'bg-yellow-500/10 text-yellow-600' };
}

const NecessidadeReposicao = () => {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [ordenarPor,   setOrdenarPor]    = useState<'criticidade' | 'descricao' | 'qtd'>('criticidade');

  const { data: bruto = [], isLoading } = useEstoqueReposicao();

  const todos = useMemo(() => {
    return [...bruto].sort((a, b) => {
      if (ordenarPor === 'criticidade') {
        const ca = calcCriticidade(a.saldoAtual, a.estoqueMinimo).ordem;
        const cb = calcCriticidade(b.saldoAtual, b.estoqueMinimo).ordem;
        return ca !== cb ? ca - cb : b.quantidadeRepor - a.quantidadeRepor;
      }
      if (ordenarPor === 'qtd') return b.quantidadeRepor - a.quantidadeRepor;
      return a.descricao.localeCompare(b.descricao, 'pt-BR');
    });
  }, [bruto, ordenarPor]);

  const total       = todos.length;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));
  const itens       = todos.slice((pagina - 1) * tamanhoPagina, pagina * tamanhoPagina);
  const firstItem   = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem    = Math.min(pagina * tamanhoPagina, total);
  const totalQtd    = todos.reduce((s, i) => s + i.quantidadeRepor, 0);

  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Necessidade de Reposição</h1>
        <p className="text-muted-foreground mt-1">Produtos com saldo abaixo do estoque mínimo configurado</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />Itens para Repor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">Produtos/locais abaixo do mínimo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-blue-500" />Qtd. Total a Repor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalQtd.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground mt-1">Unidades totais necessárias</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Produtos que Precisam de Reposição
            </CardTitle>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Ordenar por:</span>
              <Select value={ordenarPor} onValueChange={(v) => { setOrdenarPor(v as typeof ordenarPor); setPagina(1); }}>
                <SelectTrigger className="h-8 w-[150px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="criticidade">Criticidade</SelectItem>
                  <SelectItem value="qtd">Qtd. a Repor</SelectItem>
                  <SelectItem value="descricao">Nome do Produto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead className="text-right">Saldo Atual</TableHead>
                  <TableHead className="text-right">Estoque Mínimo</TableHead>
                  <TableHead className="text-right">Qtd. a Repor</TableHead>
                  <TableHead>Criticidade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : itens.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum produto abaixo do estoque mínimo</TableCell></TableRow>
                ) : (
                  itens.map((item, idx) => {
                    const criticidade = calcCriticidade(item.saldoAtual, item.estoqueMinimo);
                    return (
                      <TableRow key={`${item.subProdutoId}-${item.localEstoqueId}-${idx}`}>
                        <TableCell className="font-medium">{item.descricao}</TableCell>
                        <TableCell>{item.localDescricao}</TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {item.saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.estoqueMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right font-bold text-orange-600">
                          {item.quantidadeRepor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Badge className={criticidade.className}>{criticidade.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NecessidadeReposicao;
